import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import offlineService from '../services/offlineService';
import stocktakeOfflineService, { SYNC_STATES } from '../services/stocktakeOfflineService';

const OfflineContext = createContext(null);

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [offlineSessions, setOfflineSessions] = useState([]);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncAll();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    const handleSwUpdate = () => {
      setSwUpdateAvailable(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('sw-updated', handleSwUpdate);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('sw-updated', handleSwUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load initial state
  useEffect(() => {
    const loadState = async () => {
      const syncInfo = await offlineService.getLastSyncInfo();
      setLastSyncInfo(syncInfo);
      
      const count = await offlineService.getPendingOutboxCount();
      setPendingCount(count);

      await refreshOfflineSessions();
    };
    loadState();
  }, []);

  // Refresh list of offline stocktake sessions
  const refreshOfflineSessions = useCallback(async () => {
    const sessions = await stocktakeOfflineService.getOfflineSessionList();
    setOfflineSessions(sessions);
    // Count pending stocktake sessions too
    const pendingStocktake = sessions.filter(s => s.sync_state === SYNC_STATES.PENDING).length;
    const outboxCount = await offlineService.getPendingOutboxCount();
    setPendingCount(outboxCount + pendingStocktake);
  }, []);

  // Enter offline mode
  const enterOfflineMode = useCallback(() => {
    setIsOfflineMode(true);
  }, []);

  // Exit offline mode
  const exitOfflineMode = useCallback(() => {
    setIsOfflineMode(false);
  }, []);

  // Sync all: outbox + stocktake sessions
  const syncAll = useCallback(async () => {
    if (!navigator.onLine || isSyncing) return;
    // Don't try to sync if auth token is missing (user was logged out)
    if (!localStorage.getItem('access_token')) return;
    
    setIsSyncing(true);
    try {
      // Sync legacy outbox
      const result = await offlineService.syncOutbox();
      if (result.success) {
        await offlineService.clearSyncedOutbox();
      }
      // Sync stocktake sessions
      await stocktakeOfflineService.syncAllPendingSessions();
      await refreshOfflineSessions();
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshOfflineSessions]);

  // Sync single stocktake session
  const syncStocktakeSession = useCallback(async (sessionId) => {
    if (!navigator.onLine) return { success: false, error: 'Offline' };
    setIsSyncing(true);
    try {
      const result = await stocktakeOfflineService.syncSessionToServer(sessionId);
      await refreshOfflineSessions();
      return result;
    } finally {
      setIsSyncing(false);
    }
  }, [refreshOfflineSessions]);

  // Cache data for offline use
  const cacheDataForOffline = useCallback(async (auditorId) => {
    const result = await offlineService.cacheScheduleData(auditorId);
    if (result.success) {
      const syncInfo = await offlineService.getLastSyncInfo();
      setLastSyncInfo(syncInfo);
    }
    return result;
  }, []);

  // Format last sync time for display
  const getLastSyncDisplay = useCallback(() => {
    if (!lastSyncInfo?.timestamp) return 'Never';
    
    const syncDate = new Date(lastSyncInfo.timestamp);
    const now = new Date();
    const diffMs = now - syncDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }, [lastSyncInfo]);

  // Apply SW update (refresh to activate new version)
  const applySwUpdate = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  }, []);

  const value = {
    isOnline,
    isOfflineMode,
    lastSyncInfo,
    pendingCount,
    isSyncing,
    offlineSessions,
    swUpdateAvailable,
    enterOfflineMode,
    exitOfflineMode,
    syncAll,
    syncStocktakeSession,
    cacheDataForOffline,
    refreshOfflineSessions,
    getLastSyncDisplay,
    applySwUpdate,
    // Convenience flags
    showOfflineBanner: !isOnline || isOfflineMode,
    canSync: isOnline && pendingCount > 0 && !isSyncing,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export default OfflineContext;
